import { useIsEditorContext } from "@editor-providers";
import { throttle } from "@library/shared/utils/throttle";
import type {
  ITableCellSelection,
  ITableSettings,
} from "@library-elements/table";
import { useNewSlidesStore } from "@editor/modules/editor/stores/use-new-slides-store";
import { store, useAppDispatch, useAppSelector } from "@store";
import { setActive, setTableActiveCellsIds } from "@store/story";
import { getTableActiveCellsIds } from "@store/story/selectors";
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { expandRectToMergeBounds } from "../utils/expand-rect-to-merge-bounds";
import { tableCreateCellsSelection } from "../utils/table-create-cells-selection";
import { useFormulaPick } from "./use-formula-pick";
import { useTableCellResize } from "./use-table-cell-resize";

const computeFullySelectedColsRows = (
  activeIds: string[],
  settings: ITableSettings,
): { selectedCols: number[]; selectedRows: number[] } => {
  if (activeIds.length === 0) return { selectedCols: [], selectedRows: [] };
  const idSet = new Set(activeIds);
  const { tableCols, tableRows, tableData } = settings;
  const cols: number[] = [];
  const rows: number[] = [];
  for (let c = 0; c < tableCols; c++) {
    let all = true;
    for (let r = 0; r < tableRows; r++) {
      const cell = tableData[c + r * tableCols];
      if (!cell || !idSet.has(cell.id)) {
        all = false;
        break;
      }
    }
    if (all) cols.push(c);
  }
  for (let r = 0; r < tableRows; r++) {
    let all = true;
    for (let c = 0; c < tableCols; c++) {
      const cell = tableData[c + r * tableCols];
      if (!cell || !idSet.has(cell.id)) {
        all = false;
        break;
      }
    }
    if (all) rows.push(r);
  }
  return { selectedCols: cols, selectedRows: rows };
};

interface IProps {
  entityId: string;
  activeCellId: null | string;
  isTableActive: boolean;
  tableWrapperRef: MutableRefObject<HTMLDivElement | null>;
  elementSettings: ITableSettings;
  colsMinWidth: MutableRefObject<number[]>;
  recomputeColsMinWidth: () => void;
  setNavigationMode?: (mode: boolean) => void;
}

export const useTableSelect = ({
  entityId,
  isTableActive,
  activeCellId,
  tableWrapperRef,
  colsMinWidth,
  recomputeColsMinWidth,
  elementSettings,
  setNavigationMode,
}: IProps) => {
  "use no memo";
  const [hoverdCol, setHoverdCol] = useState<null | number>(null);
  const [hoverdRow, setHoverdRow] = useState<null | number>(null);
  const { tableRows, tableCols } = elementSettings;
  const isEditor = useIsEditorContext();
  const [selection, setSelection] = useState<ITableCellSelection | null>(null);
  const dispatch = useAppDispatch();

  const activerCellsIds = useAppSelector((state) =>
    isTableActive ? getTableActiveCellsIds(state) : null,
  );

  // Use refs for values needed in mousedown handler to avoid stale closures
  const activerCellsIdsRef = useRef(activerCellsIds);
  activerCellsIdsRef.current = activerCellsIds;
  const elementSettingsRef = useRef(elementSettings);
  elementSettingsRef.current = elementSettings;

  // Ref-picking during in-cell formula edit lives in its own hook (capture-phase
  // pointer handlers + click/contextmenu suppression).
  useFormulaPick({ entityId, tableWrapperRef });

  const {
    setResizeData,
    handleDoubleClickAutoFit,
    indicatorElRef,
    isResizingRef,
  } = useTableCellResize({
    colsMinWidth,
    elementSettings,
    tableWrapperRef,
  });

  const clearSelection = useCallback(() => {
    setSelection(null);
    dispatch(setTableActiveCellsIds([]));
  }, [setSelection, dispatch]);

  useEffect(() => {
    if (!isEditor || !isTableActive) {
      return;
    }

    const handleMouseMove = throttle((e: MouseEvent) => {
      // A column/row drag moves the pointer across cell boundaries, and every
      // hover change rebuilds `editorData` — which is a dependency of the
      // memoized TableView element, so it re-renders every cell and a
      // <Froala> per cell mid-drag. The hover affordances are meaningless
      // while resizing anyway, so hold the last value until the drag ends.
      if (isResizingRef.current) return;

      const target = e.target;

      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (!tableWrapperRef.current?.contains(target)) {
        setHoverdCol(null);
        setHoverdRow(null);
        return;
      }

      const cellEl = target.closest("[data-cell-id]");
      if (!(cellEl instanceof HTMLElement)) return;

      const col = Number(cellEl.getAttribute("data-cell-col"));
      const row = Number(cellEl.getAttribute("data-cell-row"));

      if (!Number.isNaN(col)) {
        setHoverdCol(col);
      }
      if (!Number.isNaN(row)) {
        setHoverdRow(row);
      }
    }, 100);

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      dispatch(setTableActiveCellsIds([]));
      setSelection(null);
    };
  }, [isEditor, isTableActive]);

  useEffect(() => {
    if (!isEditor || !isTableActive) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target;

      if (
        !(target instanceof HTMLElement) ||
        target.closest("[data-row-col-handle]")
      ) {
        return;
      }

      const wrapperEl = tableWrapperRef.current;
      if (
        !wrapperEl ||
        !target.closest(".mainStory") ||
        target.closest("[data-table-cell-actions]")
      ) {
        return;
      }

      if (e.button === 2) {
        // Right-click inside the existing selection must not collapse it, so the
        // context menu can act on the multi-cell selection (e.g. merge cells).
        // Previously detected via the rect overlay's bbox, but that overlay was
        // removed when per-cell borders replaced it.
        const clickedCellEl = target.closest("[data-cell-id]");
        const clickedCellId =
          clickedCellEl instanceof HTMLElement
            ? clickedCellEl.getAttribute("data-cell-id")
            : null;
        const activeIds = activerCellsIdsRef.current ?? [];
        if (clickedCellId && activeIds.includes(clickedCellId)) {
          return;
        }
      }

      if (!wrapperEl.contains(target)) {
        clearSelection();
        return;
      }

      const cellEl = target.closest("[data-cell-id]");
      if (!(cellEl instanceof HTMLElement)) return;

      const cellRect = cellEl.getBoundingClientRect();

      const isResizingCol = target.closest("[data-is-col-resize]");
      const isResizingRow =
        !isResizingCol && target.closest("[data-is-row-resize]");

      if (isResizingCol || isResizingRow) {
        e.preventDefault();
        e.stopPropagation();

        // The content-based per-col floor is no longer kept in sync with
        // every keystroke (perf). Refresh it on drag-start so manual resize
        // still can't shrink a col below its content width.
        recomputeColsMinWidth();

        // Snapshot fully-selected cols/rows before we clear the selection so
        // the resize hook can apply the drag to the whole group at once.
        const { selectedCols, selectedRows } = computeFullySelectedColsRows(
          activerCellsIdsRef.current ?? [],
          elementSettingsRef.current,
        );

        setResizeData({
          type: isResizingCol ? "col" : "row",
          cellEl,
          originalWidth: cellRect.width,
          pressPoint: { x: e.x, y: e.y },
          selectedCols: isResizingCol ? selectedCols : undefined,
          selectedRows: isResizingRow ? selectedRows : undefined,
        });

        clearSelection();
        return;
      }

      const cellId = cellEl.getAttribute("data-cell-id");
      if (!cellId) return;

      // Two-step selection (tables 2.0 in-cell elements): the first click
      // selects the cell (all existing gestures intact); a SECOND click on an
      // in-cell element inside the already-selected cell activates it (its
      // editor mounts — Text.editor gates Froala on isActive).
      const clickedElementEl = target.closest('[data-layer-type="element"]');
      if (
        clickedElementEl instanceof HTMLElement &&
        cellEl.contains(clickedElementEl) &&
        activerCellsIdsRef.current?.includes(cellId)
      ) {
        const childId = clickedElementEl.getAttribute("data-layer-id");
        const child = childId
          ? store.getState().story.flatContentLayers[childId]
          : undefined;
        if (
          childId &&
          child?.parentId === entityId &&
          (child.settings as { cellId?: string }).cellId === cellId
        ) {
          dispatch(setActive({ id: childId }));
          setNavigationMode?.(false);
          useNewSlidesStore.getState().requestFocus(childId);
          return;
        }
      }

      const currentActiveCellsIds = activerCellsIdsRef.current;
      const currentSettings = elementSettingsRef.current;
      const isAdditive = e.metaKey || e.ctrlKey;
      if (e.shiftKey && currentActiveCellsIds && currentActiveCellsIds.length > 0) {
        // Shift+click: range selection, enter navigation mode. Prevent the
        // browser's native text selection (which would highlight text across
        // the swept cells) so only the cell-range highlight shows.
        e.preventDefault();
        window.getSelection()?.removeAllRanges();
        const startCellId = currentActiveCellsIds[0];
        const { tableData, tableCols } = currentSettings;
        const startIdx = tableData.findIndex((c) => c.id === startCellId);
        const endIdx = tableData.findIndex((c) => c.id === cellId);
        if (startIdx >= 0 && endIdx >= 0) {
          const startRow = Math.floor(startIdx / tableCols);
          const startCol = startIdx % tableCols;
          const endRow = Math.floor(endIdx / tableCols);
          const endCol = endIdx % tableCols;
          // Grow the rect so any merged cell it touches is fully contained.
          const { minRow, maxRow, minCol, maxCol } = expandRectToMergeBounds(
            tableData,
            tableCols,
            {
              minRow: Math.min(startRow, endRow),
              maxRow: Math.max(startRow, endRow),
              minCol: Math.min(startCol, endCol),
              maxCol: Math.max(startCol, endCol),
            },
          );
          const selectedIds: string[] = [];
          for (let r = minRow; r <= maxRow; r++) {
            for (let c = minCol; c <= maxCol; c++) {
              selectedIds.push(tableData[r * tableCols + c].id);
            }
          }
          dispatch(setTableActiveCellsIds(selectedIds));
          setNavigationMode?.(true);
        }
      } else if (isAdditive) {
        // Cmd/Ctrl+click: toggle the clicked cell in or out of the selection.
        // Starts a multi-selection even from a single-cell state. Using
        // navigation mode keeps Froala from intercepting the click as a text
        // edit gesture.
        const existing = currentActiveCellsIds ?? [];
        const next = existing.includes(cellId)
          ? existing.filter((id) => id !== cellId)
          : [...existing, cellId];
        dispatch(setTableActiveCellsIds(next));
        setNavigationMode?.(true);
      } else {
        // Normal click: select single cell, enter edit mode
        dispatch(setTableActiveCellsIds([cellId]));
        setNavigationMode?.(false);
      }
    };


    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("dblclick", handleDoubleClickAutoFit);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("dblclick", handleDoubleClickAutoFit);
    };
  }, [
    isEditor,
    isTableActive,
    tableRows,
    tableCols,
    setResizeData,
    clearSelection,
    handleDoubleClickAutoFit,
    recomputeColsMinWidth,
  ]);

  useEffect(() => {
    if (activerCellsIds?.length === 0) {
      setSelection(null);
      return;
    }

    const wrapperEl = tableWrapperRef.current;
    if (!wrapperEl) return;

    // The rect overlay only makes visual sense for a contiguous rectangular
    // range. Cmd/Ctrl+click builds an arbitrary set of cells that may not
    // form a rect (or may be listed in click-order that doesn't match the
    // visual top-left → bottom-right). Detect this and suppress the overlay
    // — individual cells still show the per-cell outline via isInSelection.
    const ids = activerCellsIds ?? [];
    const { tableData, tableCols: cols, tableRows: rows } =
      elementSettingsRef.current;
    let minRow = Infinity;
    let maxRow = -1;
    let minCol = Infinity;
    let maxCol = -1;
    for (const id of ids) {
      const idx = tableData.findIndex((c) => c.id === id);
      if (idx < 0) continue;
      const r = Math.floor(idx / cols);
      const c = idx % cols;
      if (r < minRow) minRow = r;
      if (r > maxRow) maxRow = r;
      if (c < minCol) minCol = c;
      if (c > maxCol) maxCol = c;
    }
    const rectSize =
      maxRow >= 0 ? (maxRow - minRow + 1) * (maxCol - minCol + 1) : 0;
    const isContiguous = rectSize > 0 && ids.length === rectSize;

    if (!isContiguous) {
      setSelection(null);
      return;
    }

    // Resolve geometric corners (not DOM order: covered merge cells render no
    // <td>) to their rendered element — a covered cell resolves to its anchor.
    const renderedElFor = (cellId: string | undefined): HTMLElement | null => {
      if (!cellId) return null;
      const cell = tableData.find((c) => c.id === cellId);
      const anchorId = cell?.mergedInto ?? cellId;
      return wrapperEl.querySelector<HTMLElement>(
        `[data-cell-id="${anchorId}"]`,
      );
    };
    const startCellEl =
      renderedElFor(tableData[minRow * cols + minCol]?.id) ?? undefined;
    const endCellEl =
      renderedElFor(tableData[maxRow * cols + maxCol]?.id) ?? undefined;

    setSelection(
      tableCreateCellsSelection({
        cellsIds: ids,
        tableWrapperEl: wrapperEl,
        tableCols: cols,
        tableRows: rows,
        startCellEl,
        endCellEl,
      }),
    );
  }, [activerCellsIds]);

  useEffect(() => {
    if (!selection || !isEditor || activeCellId) {
      return;
    }
    setSelection(null);
  }, [activeCellId]);

  const updateSelection = useCallback(() => {
    if (!tableWrapperRef.current) return;

    setSelection((prev) => {
      if (!prev) return prev;
      return tableCreateCellsSelection({
        ...prev,
        tableWrapperEl: tableWrapperRef.current as HTMLElement,
        tableCols,
        tableRows,
        startCellEl: prev.startCellEl,
        endCellEl: prev.endCellEl,
      });
    });
  }, [tableRows, tableCols, tableWrapperRef]);

  return {
    selection,
    updateSelection,
    clearSelection,
    setSelection,
    hoverdRow,
    hoverdCol,
    indicatorElRef,
  };
};
